import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  OnDestroy,
  OnInit,
  WritableSignal,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  Subject,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  of,
  startWith,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';

import { AppRoutes } from '@gfe/app.paths';
import { GbeErrorCodes } from '@gfe/core/models/gbe/gbe-error-codes.enum';
import { GbeError } from '@gfe/core/models/gbe/gbe-error.model';
import {
  KpiIds,
  KpiModel,
  kpiDetailRows,
  orderAndFilterKpis
} from '@gfe/core/models/kpi.model';
import {
  ChildInfo,
  ChildInfoVisu,
  PlanDeployStatus,
  PlanMetadataModel,
  PlanMetadataVisu
} from '@gfe/core/models/plan.model';
import {
  AddPlanningSpaceResponse,
  PlanningSpaceMetadataModel
} from '@gfe/core/models/planning-space/planning-space.model';
import { CurrentLangService } from '@gfe/core/services/common/current-lang.service';
import { DeployChannelCountService } from '@gfe/core/services/deploy-plan-config/deploy-channel-count.service';
import { EntityDisplayNamesService } from '@gfe/core/services/entity-display-names/entity-display-names.service';
import { EntityIconService } from '@gfe/core/services/entity-icon/entity-icon.service';
import {
  DistinctRef,
  occurrenceText
} from '@gfe/core/services/gbe-error-parser/entity-ref-labels';
import { GbeErrorParserService } from '@gfe/core/services/gbe-error-parser/gbe-error-parser.service';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { FindingsVisibilityService } from '@gfe/core/services/plan-findings/findings-visibility.service';
import { PlanningSpaceService } from '@gfe/core/services/planning-space/planning-space.service';
import {
  AddToPlanByFilter,
  AddToPlanRequest,
  PlansService
} from '@gfe/core/services/plans/plans.service';
import {
  selectAvailableEntities,
  selectDataspaceModel
} from '@gfe/core/store/dataspace/dataspace.selectors';
import { PlanFindingsActions } from '@gfe/core/store/plan-findings/plan-findings.actions';
import {
  selectBlockingFindings,
  selectFindingsAreStale,
  selectFindingsBusy,
  selectFindingsCheckContext,
  selectFindingsPhase,
  selectOptimizeBlocked,
  selectPlanFindingsInView,
  selectPlanFindingsLive,
  selectPlanFindingsStatusInView,
  selectPlanNeverChecked
} from '@gfe/core/store/plan-findings/plan-findings.selectors';
import { UserPreferencesActions } from '@gfe/core/store/user-preferences/user-preferences.actions';
import {
  selectEntitiesOrder,
  selectKpiDecimals,
  selectKpiHidden,
  selectKpiOrder,
  selectPinnedEntities
} from '@gfe/core/store/user-preferences/user-preferences.selectors';
import { capitalizeFirst } from '@gfe/core/utils/case-conversions';
import {
  buildDeployTooltipHtml,
  computeOverallDeployStatus
} from '@gfe/core/utils/deploy-status.utils';
import { isPlanEditBlocked } from '@gfe/core/utils/plan-edit.utils';
import { isNil } from '@gfe/core/utils/shared-utils';
import { EntityComponent } from '@gfe/features/components/entities/entity/entity.component';
import { DuplicatePlanFlowComponent } from '@gfe/features/components/plan/duplicate-plan-flow/duplicate-plan-flow.component';
import { BreadcrumbNamesService } from '@gfe/shared/breadcrumb/breadcrumb-names.service';
import {
  CardConfig,
  CardsListComponent,
  CardsListComponentConfig
} from '@gfe/shared/components/cards-list/cards-list.component';
import { FilterConfiguration } from '@gfe/shared/components/filter-query-builder/filter.models';
import {
  ModalComponent,
  ModalConfig,
  ModalSize,
  ModalType
} from '@gfe/shared/components/modal/modal.component';
import {
  SearchConfig,
  ToolboxComponent,
  ToolboxSection
} from '@gfe/shared/components/toolbox/toolbox.component';
import { TooltipComponent } from '@gfe/shared/components/tooltip/tooltip.component';
import { AvailableLanguages } from '@gfe/shared/types/translation.type';

import { FilterExpr } from '@core/filters/path-filter/wire';
import {
  canDeleteJob,
  canDownloadArtifacts,
  canViewJobLogs,
  hasJobStarted
} from '@core/models/optimization-job-actions';
import {
  JobSize,
  JobStatusUtils,
  JobStatusV4,
  NotCancelableDetails,
  OptimizationRequest
} from '@core/models/optimization.model';
import { NotificationService } from '@core/services/notification/notification.service';
import { OptimizationService } from '@core/services/optimization/optimization.service';
import { ParamsService } from '@core/services/params/params.service';
import * as AuthActions from '@core/store/auth/auth.actions';
import {
  selectCanOptimize,
  selectHasUserPermission,
  selectUser
} from '@core/store/auth/auth.selectors';
import { selectPlanEditState } from '@core/store/plan-edit/plan-edit.selectors';
import { planKpisView } from '@core/store/plan-edit/plan-edit.state';
import { HOLDER_KIND_KEYS, planHolderView } from '@core/store/plan-edit/plan-holder-view';
import * as PlanActions from '@core/store/plan/plan.actions';
import * as PlanSelectors from '@core/store/plan/plan.selectors';
import { deployStatusesOf } from '@core/utils/plan/deploy-statuses';
import {
  DescribedRecipeParameter,
  describeParameterValues
} from '@core/utils/recipe-parameter-schema.utils';
import { foldForSearch } from '@core/utils/search-text.utils';

import { ButtonComponent } from '@shared/components/button/button.component';
import { OptimizationJobStatusComponent } from '@shared/components/optimization-job-status/optimization-job-status.component';
import { KpiDetailPopupService } from '@shared/services/kpi-detail-popup/kpi-detail-popup.service';
import { ReferencePopupService } from '@shared/services/reference-popup/reference-popup.service';

import { sortEntityCards } from '@features/components/entities/entities-sort.util';
import { DeployWarningsListComponent } from '@features/components/plan/deploy/deploy-warnings-list/deploy-warnings-list.component';
import { PlanEditBannerComponent } from '@features/components/plan/edit/plan-edit-banner/plan-edit-banner.component';
import { PlanEditBarComponent } from '@features/components/plan/edit/plan-edit-bar/plan-edit-bar.component';
import { PlanFindingsPanelComponent } from '@features/components/plan/findings-panel/plan-findings-panel.component';
import { OptimizationArtifactsDropdownComponent } from '@features/components/plan/optimization-artifacts-dropdown/optimization-artifacts-dropdown.component';
import { OptimizationJobLogsComponent } from '@features/components/plan/optimization-job-logs/optimization-job-logs.component';

interface AddDataModalCfg {
  planId: string;
  entityName: string;
}

@Component({
  selector: 'app-plan',
  imports: [
    TranslatePipe,
    DatePipe,
    ToolboxComponent,
    CardsListComponent,
    EntityComponent,
    ModalComponent,
    RouterLink,
    TooltipComponent,
    ButtonComponent,
    DuplicatePlanFlowComponent,
    PlanEditBannerComponent,
    PlanEditBarComponent,
    OptimizationJobLogsComponent,
    OptimizationArtifactsDropdownComponent,
    OptimizationJobStatusComponent,
    PlanFindingsPanelComponent,
    DeployWarningsListComponent,
    CdkDropList,
    CdkDrag
  ],
  providers: [DeployChannelCountService],
  templateUrl: './plan.component.html',
  styles: ``
})
export class PlanComponent implements OnInit, OnDestroy {
  cardListConfig = computed<CardsListComponentConfig>(() => {
    this.currentLang();
    return {
      noDataMessage: this.translate.instant('common.no_data'),
      pinTooltip: this.translate.instant('common.pin'),
      unpinTooltip: this.translate.instant('common.unpin'),
      onPinToggle: (card) =>
        this.store.dispatch(
          UserPreferencesActions.togglePinnedEntity({ entityName: card.id })
        ),
      onReorder: (order) =>
        this.store.dispatch(UserPreferencesActions.setEntitiesOrder({ order })),
      pinnedLabel: this.translate.instant('features.entities.pinned.title'),
      pinnedOrder: this.pinnedEntities(),
      onPinnedReorder: (order) =>
        this.store.dispatch(UserPreferencesActions.reorderPinnedEntities({ order })),
      cards: this.cards()
    };
  });

  protected readonly ICONS = ICONS;
  protected readonly ModalSize = ModalSize;
  protected readonly addDataModalConfig: ModalConfig = {
    dismissible: true,
    closeOnBackdropClick: true
  };
  protected readonly duplicateFlowOpen = signal(false);
  protected readonly entityIcons = inject(EntityIconService);
  private readonly deployChannels = inject(DeployChannelCountService);
  private readonly notificationService = inject(NotificationService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly breadcrumbNames = inject(BreadcrumbNamesService);
  private readonly paramService = inject(ParamsService);
  private readonly optimizationService = inject(OptimizationService);
  private readonly plansService = inject(PlansService);
  private readonly planningSpaceService = inject(PlanningSpaceService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();
  private readonly gbeErrorParserService = inject(GbeErrorParserService);
  private readonly referencePopup = inject(ReferencePopupService);
  private readonly currentLangService = inject(CurrentLangService);
  private readonly entityDisplayNamesService = inject(EntityDisplayNamesService);

  protected readonly currentLang = this.currentLangService.currentLang;
  private readonly toolboxTranslateKeyPrefix = 'features.plan.toolbox.';

  protected addDataModalCfg: AddDataModalCfg | undefined = undefined;

  protected showOptimizationModal = false;
  private readonly kpiOrder = this.store.selectSignal(selectKpiOrder);
  private readonly kpiHidden = this.store.selectSignal(selectKpiHidden);
  private readonly kpiDecimals = this.store.selectSignal(selectKpiDecimals);

  /** The KPIs to show and how they are marked: a live session's replace the stored plan's. */
  // Every watch patch changes the plan-edit state; only new KPIs or a new mode should rebuild the cards.
  private readonly kpisView = computed(
    () =>
      planKpisView(this.planEditState().watch, this.planId(), this.planMetadata()?.kpis),
    { equal: (a, b) => a.kpis === b.kpis && a.mode === b.mode }
  );
  protected readonly kpisMode = computed(() => this.kpisView().mode);
  protected readonly kpisMuted = computed(
    () => this.kpisMode() === 'saved-plan' || this.kpisMode() === 'provisional'
  );

  /** All KPIs for the current plan, formatted with the user's decimals. */
  private readonly allKpis = computed<KpiModel[]>(() =>
    this.kpisView().kpis.map((kpi) => new KpiModel(kpi, this.kpiDecimals()))
  );

  /** KPIs actually rendered on the panel: hidden ones removed, ordering applied. */
  protected kpis = computed<KpiModel[]>(() =>
    orderAndFilterKpis(this.allKpis(), this.kpiOrder(), this.kpiHidden())
  );
  protected searchOnAddEntity = signal<string>('');
  protected searchOnCardEntities = signal<string>('');

  searchCfg: SearchConfig = {
    active: true,
    type: 'simple',
    shortcut: true
  };

  protected readonly entityTypeLabels = this.entityDisplayNamesService.singularDisplayNames;

  constructor() {
    inject(DestroyRef).onDestroy(inject(FindingsVisibilityService).show());

    effect(() => {
      const plan = this.planMetadata();
      if (plan) {
        this.breadcrumbNames.set('plan', plan.id, {
          fr: plan.name || 'Plan sans nom',
          en: plan.name || 'Unnamed Plan',
          es: plan.name || 'Plan sin nombre',
          'pt-BR': plan.name || 'Plano sem nome'
        });
      }
    });

    effect(() => {
      if (isNil(this.dataSpaceId)) {
        this.dataSpaceId = this.dataspace()?.id;
        return;
      }

      if (this.dataSpaceId !== this.dataspace()?.id) {
        setTimeout(() => {
          this.router.navigate(['ds', this.dataspace()!.id, 'planning-spaces']);
        });
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape':
        this.closeEntityModal();

        if (this.showOptimizationModal && !this.hasUnfinishedJob()) {
          this.closeOptimizationModal();
        }
        break;
    }
  }
  private allEntityMetadatas$ = toObservable(this.searchOnAddEntity).pipe(
    switchMap((search) =>
      this.store.select(selectAvailableEntities).pipe(
        map((entities) => {
          if (!search || search.trim() === '') return entities;
          const foldedSearch = foldForSearch(search);
          return entities.filter(
            (entity) =>
              foldForSearch(entity.displayName.en).includes(foldedSearch) ||
              foldForSearch(entity.displayName.fr).includes(foldedSearch) ||
              foldForSearch(entity.name).includes(foldedSearch)
          );
        })
      )
    )
  );

  private allEntityMetadatas = toSignal(this.allEntityMetadatas$, { initialValue: [] });

  protected readonly KPI_PREVIEW_MAX = 3;

  private readonly kpiDetailPopup = inject(KpiDetailPopupService);

  protected hasKpiDetail(kpi: KpiModel): boolean {
    return kpiDetailRows(kpi).length > 0;
  }

  protected toggleKpiDetail(kpi: KpiModel, kpiCard: HTMLElement): void {
    if (!this.hasKpiDetail(kpi)) return;
    this.kpiDetailPopup.toggle(kpiCard, {
      labelKey: kpi.i18nKey,
      rows: kpiDetailRows(kpi),
      placement: 'right-start'
    });
  }

  /** Only a key pressed on the card itself: Space on the hide button must not open the card. */
  protected onKpiCardKeydown(kpi: KpiModel, event: Event): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.toggleKpiDetail(kpi, event.currentTarget as HTMLElement);
  }

  /**
   * Hide a KPI from the panel. Re-showing is done from the preferences page. Closes an open
   * KPI card too, as that card may be anchored on the KPI being removed.
   */
  protected hideKpi(kpiId: KpiIds, event: MouseEvent): void {
    event.stopPropagation();
    this.kpiDetailPopup.close();
    this.store.dispatch(UserPreferencesActions.toggleKpiVisibility({ kpiId }));
  }

  /**
   * Persist a new KPI ordering after a drag on the panel. The panel only shows
   * visible KPIs, so we translate the drop into the full ordering (existing
   * stored order + any backend KPI not yet ranked) to keep hidden/unranked ids
   * in place, then move the dragged id relative to its new neighbour.
   */
  protected onKpiDrop(event: CdkDragDrop<KpiModel[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const visible = this.kpis();
    const draggedId = visible[event.previousIndex]?.id;
    const targetId = visible[event.currentIndex]?.id;
    if (!draggedId || !targetId) return;

    const stored = this.kpiOrder();
    const merged: KpiIds[] = [...stored];
    for (const kpi of this.allKpis()) {
      if (!merged.includes(kpi.id)) merged.push(kpi.id);
    }

    const from = merged.indexOf(draggedId);
    const to = merged.indexOf(targetId);
    if (from < 0 || to < 0) return;
    moveItemInArray(merged, from, to);
    this.store.dispatch(UserPreferencesActions.setKpiOrder({ order: merged }));
  }

  ngOnInit(): void {
    // The optimize gate reads permissions cached at app init. An internal admin may have
    // granted `optimize` since then, and the disabled button gives the user no way to
    // discover that, so re-read them on the way into the page that offers the action.
    this.store.dispatch(AuthActions.refreshPermissions());

    this.activatedRoute.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap) => {
      const planId = paramMap.get(AppRoutes.plan.plans.view.params.planId);
      if (planId) {
        this.store.dispatch(PlanActions.fetchPlans({ id: planId }));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startOptimization(paramId: number, planId: string): void {
    const request: OptimizationRequest = {
      paramId: paramId,
      planId: planId
    };

    this.optimizationService
      .startOptimization(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showOptimizationModal = true;
          this.notificationService.success(
            this.translate.instant('optimization.message.started'),
            '',
            3000
          );
        },
        error: (err) => {
          console.error('Error in start of optimization :', err);
          if (err?.status === 403) {
            // The permission was revoked between page load and this click. Re-read it so the
            // button settles into the disabled state that matches the server.
            this.store.dispatch(AuthActions.refreshPermissions());
            this.notificationService.error(
              this.translate.instant(
                this.toolboxTranslateKeyPrefix + 'optimize.tooltip.not_enabled'
              ),
              ''
            );
            return;
          }
          this.notificationService.error(
            this.translate.instant('optimization.message.error'),
            ''
          );
        }
      });
  }

  protected requestCheck(): void {
    this.store.dispatch(PlanFindingsActions.checkAsked());
  }

  isJobFinished(child: ChildInfoVisu): boolean {
    return child.jobStatus === JobStatusV4.OPTIMIZATION_FINISHED;
  }

  private readonly currentUser = this.store.selectSignal(selectUser);
  private readonly hasOptimizationLogsPermission = this.store.selectSignal(
    selectHasUserPermission('optimization_logs')
  );
  protected readonly canViewJobLogs = computed(() =>
    canViewJobLogs(this.currentUser()?.role, this.hasOptimizationLogsPermission())
  );
  private readonly hasOptimizationArtifactsPermission = this.store.selectSignal(
    selectHasUserPermission('optimization_artifacts')
  );
  /** The role/permission half only; the template adds the per-job started check. */
  protected readonly canViewJobArtifacts = computed(() =>
    canDownloadArtifacts(
      this.currentUser()?.role,
      this.hasOptimizationArtifactsPermission(),
      JobStatusV4.RUNNING
    )
  );
  private readonly canOptimize = this.store.selectSignal(selectCanOptimize);
  protected readonly showLogsModal = signal(false);
  protected readonly selectedLogsJobId = signal<string | null>(null);

  protected hasJobStarted(child: ChildInfo): boolean {
    return hasJobStarted(child.jobStatus);
  }

  protected openJobLogs(child: ChildInfo): void {
    this.selectedLogsJobId.set(child.jobId);
    this.showLogsModal.set(true);
  }

  protected closeJobLogs(): void {
    this.showLogsModal.set(false);
    this.selectedLogsJobId.set(null);
  }

  protected readonly showCancelJobModal = signal(false);
  protected readonly pendingCancelJob = signal<ChildInfo | null>(null);
  protected readonly cancelInFlightJobIds = signal<ReadonlySet<string>>(new Set());

  /**
   * A set rather than one slot: requests on two rows can overlap, and a single slot would
   * re-enable the first row's button the moment the second one started, then clear the guard
   * on both as soon as either response landed.
   */
  private setInFlight(
    ids: WritableSignal<ReadonlySet<string>>,
    jobId: string,
    inFlight: boolean
  ): void {
    ids.update((current) => {
      const next = new Set(current);
      if (inFlight) {
        next.add(jobId);
      } else {
        next.delete(jobId);
      }
      return next;
    });
  }

  /**
   * Jobs the back end refused as impossible to stop, because they run on a shared worker it
   * cannot interrupt. That is a permanent property of the job, so the button never comes back.
   */
  private readonly notCancelableJobIds = signal<ReadonlySet<string>>(new Set());

  /**
   * The server's verdict, in two halves it reports separately: whether this user may cancel jobs
   * in this plan at all (their `run_application` grant and the optimize permission, neither
   * readable from the front end) and whether the job's current shape can still be stopped. The
   * shape half rides every status event, so no separate status check is needed here — a stale
   * verdict would mean a status that is stale with it.
   */
  protected canCancelJob(child: ChildInfo): boolean {
    return (
      this.planMetadata()?.canCancelJobs === true &&
      child.isCancelable === true &&
      !this.notCancelableJobIds().has(child.jobId)
    );
  }

  protected requestCancelJob(child: ChildInfo): void {
    this.pendingCancelJob.set(child);
    this.showCancelJobModal.set(true);
  }

  protected closeCancelJobModal(): void {
    this.showCancelJobModal.set(false);
    this.pendingCancelJob.set(null);
  }

  protected readonly cancelJobModalConfig = computed<ModalConfig>(() => {
    this.currentLang();
    return {
      type: ModalType.WARNING,
      dismissible: true,
      closeOnBackdropClick: true,
      actions: [
        {
          // Not `common.cancel`: beside "Cancel optimization", a button labelled "Cancel" reads
          // as the same action, and this one is irreversible.
          label: this.translate.instant('features.plan.jobs.cancel.keepRunning'),
          callback: () => this.closeCancelJobModal(),
          color: 'cancel'
        },
        {
          label: this.translate.instant('features.plan.jobs.cancel.button'),
          callback: () => this.confirmCancelJob(),
          color: ModalType.ERROR
        }
      ]
    };
  });

  protected confirmCancelJob(): void {
    const child = this.pendingCancelJob();
    if (isNil(child)) {
      return;
    }

    this.closeCancelJobModal();
    this.setInFlight(this.cancelInFlightJobIds, child.jobId, true);

    this.optimizationService
      .cancelOptimization$(child.jobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.setInFlight(this.cancelInFlightJobIds, child.jobId, false);
          // The websocket delivers the same status, but it may be down, and until the row
          // reads CANCELED it keeps offering a button for a job that is already stopped. No
          // toast here: the websocket raises one for whoever launched the job, and the row
          // flipping to CANCELED is the feedback for everyone else, this user included when
          // they are cancelling someone else's run.
          this.store.dispatch(PlanActions.optimizationUpdated({ result }));
        },
        error: (err: GbeError) => {
          this.setInFlight(this.cancelInFlightJobIds, child.jobId, false);
          this.reportCancelFailure(child, err);
        }
      });
  }

  private reportCancelFailure(child: ChildInfo, error: GbeError): void {
    const cancelPrefix = 'features.plan.jobs.cancel.errors.';

    if (error?.status === 403) {
      // The grant was revoked between the plan fetch and this click. The endpoint answers to
      // the data space's `run_application` action, which the auth store does not model, and
      // every button on the page is gated on the plan's `canCancelJobs` — so the plan has to be
      // re-read too, or the buttons stay and every further click 403s.
      this.store.dispatch(AuthActions.refreshPermissions());
      const planId = this.planId();
      if (!isNil(planId)) {
        this.store.dispatch(PlanActions.fetchPlans({ id: planId }));
      }
      this.notificationService.error(
        this.translate.instant(cancelPrefix + 'forbidden'),
        ''
      );
      return;
    }

    if (error?.errorCode === GbeErrorCodes.OPTIMIZATION_NOT_CANCELABLE) {
      const details = error.details as NotCancelableDetails | undefined;
      const refusedAt = details?.status;

      if (!isNil(refusedAt) && JobStatusUtils.isTerminalStatus(refusedAt)) {
        // The run finished first. The websocket carries the real status, so leave the row.
        this.notificationService.error(
          this.translate.instant(cancelPrefix + 'alreadyFinished'),
          ''
        );
        return;
      }

      // A LARGE job is its own Kubernetes Job, which the back end can still delete. Refused at
      // a non-terminal status, it only lost a race — the status moved out of the cancelable
      // set between the read and the write (RETRY, or another cancel holding the job's lock) —
      // so the next click may well succeed and the button has to stay.
      if (details?.job_size === JobSize.LARGE) {
        this.notificationService.error(this.translate.instant(cancelPrefix + 'retry'), '');
        return;
      }

      // Anything else already computes inside the shared worker pod, which the back end has no
      // way to interrupt. That is fixed for the life of the job, so no retry can succeed.
      this.notCancelableJobIds.update((ids) => new Set(ids).add(child.jobId));
      this.notificationService.error(
        this.translate.instant(cancelPrefix + 'notStoppable'),
        ''
      );
      return;
    }

    if (error?.status === 404) {
      this.notificationService.error(this.translate.instant(cancelPrefix + 'notFound'), '');
      return;
    }

    this.notificationService.error(this.translate.instant(cancelPrefix + 'generic'), '');
  }

  protected readonly showDeleteJobModal = signal(false);
  protected readonly pendingDeleteJob = signal<ChildInfo | null>(null);
  /**
   * The guard matters more here than on the cancel: the delete is deliberately non-idempotent,
   * so a second request for a job already deleted answers 404 and reports a failure for
   * something that succeeded.
   */
  protected readonly deleteInFlightJobIds = signal<ReadonlySet<string>>(new Set());

  /**
   * Set when the server has refused a delete as forbidden. The endpoint answers to the role,
   * which is a property of the user rather than of one job, so a single refusal withdraws every
   * button: the cached role says otherwise and cannot be re-read from here.
   */
  private readonly deleteForbidden = signal(false);

  protected canDeleteJob(child: ChildInfo): boolean {
    return (
      !this.deleteForbidden() && canDeleteJob(this.currentUser()?.role, child.jobStatus)
    );
  }

  protected requestDeleteJob(child: ChildInfo): void {
    this.pendingDeleteJob.set(child);
    this.showDeleteJobModal.set(true);
  }

  protected closeDeleteJobModal(): void {
    this.showDeleteJobModal.set(false);
    this.pendingDeleteJob.set(null);
  }

  protected readonly deleteJobModalConfig = computed<ModalConfig>(() => {
    this.currentLang();
    return {
      type: ModalType.WARNING,
      dismissible: true,
      closeOnBackdropClick: true,
      actions: [
        {
          label: this.translate.instant('common.cancel'),
          callback: () => this.closeDeleteJobModal(),
          color: 'cancel'
        },
        {
          label: this.translate.instant('features.plan.jobs.delete.button'),
          callback: () => this.confirmDeleteJob(),
          color: ModalType.ERROR
        }
      ]
    };
  });

  protected confirmDeleteJob(): void {
    const child = this.pendingDeleteJob();
    if (isNil(child)) {
      return;
    }

    this.closeDeleteJobModal();
    this.setInFlight(this.deleteInFlightJobIds, child.jobId, true);

    this.optimizationService
      .deleteOptimization$(child.jobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.setInFlight(this.deleteInFlightJobIds, child.jobId, false);
          // GBE broadcasts the delete to the data space, so this dispatch is only what keeps
          // the row from surviving until the next fetch when that channel is down. The echo
          // that follows drops the same row again, which the reducer absorbs.
          this.dropDeletedJobRow(child);
          this.notificationService.success(
            this.translate.instant('features.plan.jobs.delete.success'),
            '',
            3000
          );
        },
        error: (err: GbeError) => {
          this.setInFlight(this.deleteInFlightJobIds, child.jobId, false);
          this.reportDeleteFailure(child, err);
        }
      });
  }

  private dropDeletedJobRow(child: ChildInfo): void {
    const planId = this.planId();
    if (isNil(planId)) {
      return;
    }
    this.store.dispatch(PlanActions.optimizationDeleted({ jobId: child.jobId, planId }));
  }

  private reportDeleteFailure(child: ChildInfo, error: GbeError): void {
    // `check-missing-translations` resolves key prefixes through one flat, unscoped map of
    // identifier names, so naming this `prefix` too would shadow the cancel one and report
    // every cancel key as missing. Keep the two names distinct.
    const deletePrefix = 'features.plan.jobs.delete.errors.';

    if (error?.status === 403) {
      // The gate is the user's role, which `refreshPermissions` does not fetch — it patches the
      // permission map only — and no plan field carries it either. So withdraw the buttons here
      // instead: every one of them is gated on the same stale role and would 403 in turn.
      this.deleteForbidden.set(true);
      this.notificationService.error(
        this.translate.instant(deletePrefix + 'forbidden'),
        ''
      );
      return;
    }

    if (error?.errorCode === GbeErrorCodes.OPTIMIZATION_NOT_DELETABLE) {
      // The job left ERROR for RETRY between the render and the click.
      this.notificationService.error(
        this.translate.instant(deletePrefix + 'notDeletable'),
        ''
      );
      return;
    }

    if (error?.status === 404) {
      // Already deleted — a second tab, or a double-click that raced itself. The state the
      // click asked for already holds, so drop the row rather than leaving one that answers 404
      // to every further attempt. Reported as information, not an error: nothing went wrong for
      // this user, they simply were not the one who did it.
      this.dropDeletedJobRow(child);
      this.notificationService.info(
        this.translate.instant(deletePrefix + 'notFound'),
        '',
        3000
      );
      return;
    }

    this.notificationService.error(this.translate.instant(deletePrefix + 'generic'), '');
  }

  protected childrenSuccessCount = computed(
    () =>
      this.planMetadata()?.childrenInfo?.filter((c) =>
        JobStatusUtils.isStatusFinished(c.jobStatus)
      ).length ?? 0
  );

  protected childrenPendingCount = computed(
    () =>
      this.planMetadata()?.childrenInfo?.filter((c) =>
        JobStatusUtils.isUnfinishedStatus(c.jobStatus)
      ).length ?? 0
  );

  protected childrenErrorCount = computed(
    () =>
      this.planMetadata()?.childrenInfo?.filter((c) =>
        JobStatusUtils.isErrorStatus(c.jobStatus)
      ).length ?? 0
  );

  protected closeOptimizationModal(): void {
    this.showOptimizationModal = false;
  }

  /**
   * Whether any child job of the plan is still running. Distinct from
   * `optimizeDisabled`/`editDisabled`, which also fold in findings-driven and
   * deployment-driven reasons a plan can't be touched right now.
   */
  private readonly hasUnfinishedJob = computed(() =>
    (this.planMetadata()?.childrenInfo ?? []).some((child) =>
      JobStatusUtils.isUnfinishedStatus(child.jobStatus)
    )
  );

  private readonly planEditState = this.store.selectSignal(selectPlanEditState);
  protected readonly holderView = computed(() =>
    planHolderView(this.planEditState(), this.currentUser()?.id, this.planId(), null)
  );
  protected readonly heldForEdit = computed(() => this.holderView() !== null);

  protected optimizeDisabled = computed(() => {
    const plan = this.planMetadata();
    return (
      isNil(plan) ||
      !this.canOptimize() ||
      this.optimizeBlocked() ||
      this.hasUnfinishedJob() ||
      this.heldForEdit()
    );
  });

  protected editDisabled = computed(() => {
    const plan = this.planMetadata();
    return isNil(plan) || isPlanEditBlocked(plan) || this.heldForEdit();
  });

  private entityMetadata = this.store.selectSignal(selectAvailableEntities);
  private pinnedEntities = this.store.selectSignal<string[]>(selectPinnedEntities);
  protected entitiesOrder = this.store.selectSignal<string[]>(selectEntitiesOrder);

  private dataspace = toSignal(
    this.store.select(selectDataspaceModel).pipe(filter((x) => !isNil(x)))
  );
  private dataSpaceId?: number;
  protected planId = computed(() => {
    return this.planMetadata()?.id ?? null;
  });

  protected planMetadata = this.store.selectSignal(PlanSelectors.selectPlan);
  protected readonly findingsLive = this.store.selectSignal(selectPlanFindingsLive);
  protected readonly findings = this.store.selectSignal(selectPlanFindingsInView);
  protected readonly findingsAreStale = this.store.selectSignal(selectFindingsAreStale);
  protected readonly findingsPhase = this.store.selectSignal(selectFindingsPhase);
  private readonly findingsCheckContext = this.store.selectSignal(
    selectFindingsCheckContext
  );
  protected readonly blockingFindings = this.store.selectSignal(selectBlockingFindings);
  protected readonly optimizeBlocked = this.store.selectSignal(selectOptimizeBlocked);
  protected readonly planNeverChecked = this.store.selectSignal(selectPlanNeverChecked);
  protected readonly findingsBusy = this.store.selectSignal(selectFindingsBusy);
  protected readonly findingsStatus = this.store.selectSignal(
    selectPlanFindingsStatusInView
  );
  private readonly deployOverrides = this.store.selectSignal(
    PlanSelectors.selectDeployStatusOverrides
  );
  protected params = toSignal(
    toObservable(this.planMetadata).pipe(
      map((metadata) => metadata?.solver?.id ?? null),
      distinctUntilChanged(),
      switchMap((solverId) => {
        if (!solverId) return of(null);
        return this.paramService.getParamsBySolverId$(Number(solverId)).pipe(
          startWith(null),
          catchError((error) => {
            console.error('Error in parameters:', error);
            return of(null);
          })
        );
      })
    ),
    { initialValue: null }
  );

  /**
   * The plan's planning space, read live rather than taken from the plan. A plan only ever
   * carries the copy it was created with, and no route updates it, so an edit made on the
   * planning space would otherwise never reach this page — and `isInternal` is not on the
   * plan at all.
   *
   * `undefined` means "not read yet, or the read failed".
   */
  private readonly planningSpace = toSignal<PlanningSpaceMetadataModel | undefined>(
    toObservable(this.planMetadata).pipe(
      map((metadata) => metadata?.planningSpace?.id ?? null),
      distinctUntilChanged(),
      switchMap((planningSpaceId) => {
        if (isNil(planningSpaceId)) {
          return of(undefined);
        }
        return this.planningSpaceService.fetchOne$({ planningSpaceId }).pipe(
          startWith(undefined),
          catchError((error) => {
            console.error('Error fetching planning space:', error);
            return of(undefined);
          })
        );
      })
    ),
    { initialValue: undefined }
  );

  /**
   * The plan's own copy stands in only until the space has been read; a description cleared
   * on the planning space stays an empty string, so it reads as absent instead of falling
   * back to the stale text.
   */
  protected readonly planDescription = computed<string>(() => {
    const space = this.planningSpace();
    if (!isNil(space)) {
      return space.description ?? '';
    }
    return this.planMetadata()?.description ?? '';
  });

  /**
   * Deploying a plan in an internal space is refused for every role, ROOT included, and it
   * answers 404 on the plan rather than 403 — staff can see the space, so an enabled Deploy
   * would hand them a bare "not found". The remedy is to make the space visible again.
   *
   * A failed read leaves this false: only a positive `isInternal` blocks, so a transient
   * error cannot lock deployment behind a reason that may not hold.
   */
  protected readonly planningSpaceIsInternal = computed(
    () => this.planningSpace()?.isInternal === true
  );

  protected cards = computed<CardConfig[]>(() => {
    const currentLang = this.currentLang() as AvailableLanguages;
    const search = foldForSearch(this.searchOnCardEntities());
    const pinned = new Set(this.pinnedEntities());

    const plan = this.planMetadata();
    if (isNil(plan)) {
      return [];
    }

    // Locale-aware grouping (e.g. "4 218" in fr, "4,218" in en) for label values.
    const numberFormat = new Intl.NumberFormat(currentLang);

    const matching = this.entityMetadata()
      .map((entity) => ({
        id: entity.name,
        title: entity.displayName?.[currentLang] || entity.name || 'Unknown',
        description: entity.description?.[currentLang] || '',
        tooltip: entity.description?.[currentLang] || '',
        iconClass: this.entityIcons.getEntityIcon(entity.name),
        isPinned: pinned.has(entity.name),
        route: entity.name,
        labels: this.buildLabels(entity.name.toLowerCase(), plan, numberFormat),
        hasAccent: undefined
      }))
      .filter((entity) => {
        const normalizedTitle = foldForSearch(entity.title);
        const normalizedDescr = foldForSearch(entity.description);
        return normalizedTitle.includes(search) || normalizedDescr.includes(search);
      });

    return sortEntityCards(matching, this.entitiesOrder(), currentLang);
  });

  protected deployStatuses = computed<PlanDeployStatus[]>(() =>
    deployStatusesOf(this.planMetadata() ?? null, this.deployOverrides())
  );

  protected overallDeployStatus = computed(() =>
    computeOverallDeployStatus(this.deployStatuses(), this.deployChannels.enabledCount())
  );

  /**
   * The info card wears the deployed purple on the same terms as the status beside it:
   * every channel out. The REST `isDeployed` is deliberately not consulted — it is set
   * when a deploy *starts* and never cleared, so a failed or half-finished deploy would
   * paint the card deployed while the row under it says otherwise. Reading the statuses
   * also keeps the card live: a deploy-plan:status event repaints it without a refetch.
   */
  protected planIsDeployed = computed<boolean>(
    () => this.overallDeployStatus() === 'SUCCESS'
  );

  /**
   * Channels carrying non-blocking warnings (e.g. SUCCESS-with-warnings). A failed
   * channel is excluded: an amber panel would report a failure as a warning, and the
   * deploy page owns everything a failed deploy has to say.
   */
  protected deployWarnings = computed<PlanDeployStatus[]>(() =>
    this.deployStatuses().filter(
      (s) => s.status !== 'ERROR' && (s.warnings?.length ?? 0) > 0
    )
  );

  /** The deploy-warnings panel is expanded by default when the page loads. */
  protected deployWarningsPanelExpanded = signal(true);

  protected onOpenReference(event: { ref: DistinctRef; anchorEl: HTMLElement }): void {
    if (event.ref.uid === null) return;
    this.referencePopup.open(event.anchorEl, {
      uid: event.ref.uid,
      targetEntityType: event.ref.entityType,
      column: '',
      row: undefined
    });
  }

  protected deployTooltipContent = computed<string>(() =>
    buildDeployTooltipHtml(this.deployStatuses(), this.currentLang())
  );

  /**
   * Navigate to the deploy page, the single home for deploy operations (edit
   * channel configs + per-channel retry).
   */
  protected goToDeployPage(): void {
    const dataspaceId = this.dataspace()?.id;
    const planId = this.planId();
    const planningSpaceId = this.activatedRoute.parent?.snapshot.paramMap.get(
      AppRoutes.plan.plans.params.planningSpaceId
    );

    if (isNil(dataspaceId) || isNil(planId) || isNil(planningSpaceId)) {
      console.error('[goToDeployPage] Missing required route params');
      return;
    }

    this.router.navigate([
      'ds',
      dataspaceId,
      AppRoutes.plan.root,
      planningSpaceId,
      'plan',
      planId,
      AppRoutes.plan.plans.view.deploy.path
    ]);
  }

  /**
   * The recipe parameter values the plan was created with, labelled for display.
   * Empty for every plan created from a recipe without parameters.
   */
  protected recipeParameterEntries = computed<DescribedRecipeParameter[]>(() => {
    this.currentLang();
    return describeParameterValues(this.planMetadata()?.recipeParameters, (key) =>
      this.translate.instant(key)
    );
  });

  protected planMetadataVisu = computed<PlanMetadataVisu | null>(() => {
    const metadata = this.planMetadata();
    if (!metadata) return null;

    return {
      ...metadata,
      childrenInfoVisu: metadata.childrenInfo?.map((child, index) => {
        return {
          ...child,
          jobVisuId: `${index + 1}`,
          statusVisu: this.getStatusMessage(child.jobStatus)
        };
      })
    };
  });

  private getStatusMessage(status: JobStatusV4): string {
    if (status === JobStatusV4.OPTIMIZATION_FINISHED) {
      return this.translate.instant('optimization.status.completed');
    }
    if (JobStatusUtils.isUnfinishedStatus(status)) {
      return this.translate.instant('optimization.status.in_progress');
    }
    if (JobStatusUtils.isBlockedStatus(status)) {
      return this.translate.instant('optimization.status.blocked');
    }
    if (status === JobStatusV4.CANCELED) {
      return this.translate.instant('optimization.status.canceled');
    }
    return this.translate.instant('optimization.status.error');
  }

  protected isEmpty = computed(() => {
    const metadatas = this.entityMetadata();
    return metadatas.length === 0;
  });

  closeEntityModal(): void {
    this.addDataModalCfg = undefined;
  }

  onPlanDuplicated(response: AddPlanningSpaceResponse): void {
    // Navigate to the freshly-created copy (sibling route of the current plan).
    this.router.navigate(['../', response.plan.id], {
      relativeTo: this.activatedRoute
    });
  }

  toolboxSections = computed<ToolboxSection[]>(() => {
    const lang = this.currentLang() as AvailableLanguages;
    const metadatas = this.allEntityMetadatas();
    const paramsArray = this.params()?.result ?? [];

    const planId = this.planId();
    if (isNil(planId)) {
      return [];
    }

    const paramsForOptimize = paramsArray.map((params) => {
      const name = params.name;
      const id = params.id;

      return {
        label: name || `Parameter ${id}`,
        action: () => {
          this.startOptimization(id, planId);
        }
      };
    });

    return [
      {
        id: 'edit-data',
        items: [
          {
            id: 'add',
            icon: ICONS.plus,
            searchConfig: {
              placeholder: this.translate.instant('common.search_placeholder')
            },
            tooltip: this.translate.instant(
              this.toolboxTranslateKeyPrefix + 'add_data.tooltip'
            ),
            items: metadatas.map((metadata) => {
              return {
                label: metadata.displayName[lang],
                action: () => {
                  const planId = this.planId();
                  if (isNil(planId)) {
                    return;
                  }
                  this.addDataModalCfg = {
                    entityName: metadata.name,
                    planId
                  };
                },
                iconClass: this.entityIcons.getEntityIcon(metadata.name)
              };
            }),
            disabled: this.editDisabled()
          },
          {
            id: 'duplicate',
            icon: ICONS.copy,
            disabled: isNil(this.planMetadata()),
            tooltip: this.translate.instant('common.duplicate'),
            callback: () => {
              if (isNil(this.planMetadata())) {
                return;
              }
              this.duplicateFlowOpen.set(true);
            }
          },
          {
            id: 'check',
            icon: ICONS.arrowsClockwise,
            disabled: isNil(this.findingsCheckContext()),
            indicator: this.findingsAreStale(),
            tooltip: this.translate.instant(
              this.toolboxTranslateKeyPrefix +
                (this.findingsAreStale() ? 'check.stale_tooltip' : 'check.tooltip')
            ),
            callback: () => this.requestCheck()
          },
          {
            id: 'validate',
            icon: ICONS.paperPlaneTilt,
            disabled: this.planningSpaceIsInternal(),
            tooltip: this.translate.instant(
              this.toolboxTranslateKeyPrefix + this.deployTooltipKey()
            ),
            callback: () => this.goToDeployPage()
          }
        ]
      },
      {
        id: 'main-action',
        items: [
          {
            id: 'optimize',
            label: this.translate.instant(
              this.toolboxTranslateKeyPrefix + 'optimize.label'
            ),
            callToAction: true,
            icon: ICONS.sparkle,
            disabled: this.optimizeDisabled(),
            tooltip: this.getOptimizeTooltip(),
            items: this.optimizeDisabled() ? [] : paramsForOptimize
          }
        ]
      }
    ];
  });

  /** Ahead of the deployed/not-deployed split: being internal is why the entry is disabled. */
  private deployTooltipKey(): string {
    if (this.planningSpaceIsInternal()) {
      return 'deploy.internal_tooltip';
    }
    return this.planMetadata()?.isDeployed ? 'deploy.settings_tooltip' : 'deploy.tooltip';
  }

  private getOptimizeTooltip(): string {
    // Ahead of the transient reasons: this one holds for the whole session.
    if (!this.canOptimize()) {
      return this.translate.instant(
        this.toolboxTranslateKeyPrefix + 'optimize.tooltip.not_enabled'
      );
    }
    if (this.hasUnfinishedJob()) {
      return this.translate.instant(
        this.toolboxTranslateKeyPrefix + 'optimize.tooltip.in_progress'
      );
    }
    const holder = this.holderView();
    if (holder) {
      return this.translate.instant(
        this.toolboxTranslateKeyPrefix +
          'optimize.tooltip.held.' +
          HOLDER_KIND_KEYS[holder.kind],
        { name: holder.name }
      );
    }
    const blocking = this.blockingFindings();
    if (blocking.length > 0) {
      const key = this.findingsAreStale()
        ? 'optimize.tooltip.blocked_stale'
        : 'optimize.tooltip.blocked';
      return this.translate.instant(this.toolboxTranslateKeyPrefix + key, {
        count: blocking.length
      });
    }
    return this.translate.instant(this.toolboxTranslateKeyPrefix + 'optimize.label');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDataAdded(data: { entities: any[]; entityTypeName: string; planId: string }): void {
    if (!data) {
      return;
    }
    const request: AddToPlanRequest = {
      entityType: data.entityTypeName,
      planId: data.planId,
      uids: data.entities.map((entity) => entity.uid)
    };
    this.plansService
      .addToPlan$(request)
      .pipe(
        tap(() => {
          this.closeEntityModal();
          this.store.dispatch(PlanActions.fetchPlans({ id: data.planId }));
        })
      )
      .subscribe({
        next: () => {},
        error: (error) => {
          console.error('Error loading add to plan', error);
        }
      });
  }

  onDataAddedByFilter(data: {
    entityType: string;
    planId: string;
    filters?: FilterConfiguration;
    search?: string;
    pathFilter?: FilterExpr;
  }): void {
    if (!data) {
      return;
    }
    const request: AddToPlanByFilter = {
      entityType: data.entityType.toUpperCase(),
      planId: data.planId,
      filters: data.filters,
      search: data.search,
      ...(data.pathFilter ? { pathFilter: data.pathFilter } : {})
    };
    this.plansService
      .addToPlanByFilter$(request)
      .pipe(
        tap(() => {
          this.closeEntityModal();
          this.store.dispatch(PlanActions.fetchPlans({ id: data.planId }));
        })
      )
      .subscribe({
        next: () => {},
        error: (error) => {
          if (
            [GbeErrorCodes.MIN_ITEMS_ERROR, GbeErrorCodes.MAX_ITEMS_ERROR].includes(
              error.errorCode
            )
          ) {
            const views = this.gbeErrorParserService.resolveError(error);
            this.notificationService.warning(
              views[0]?.title ?? error.errorCode ?? '',
              views
                .flatMap((view) => view.occurrences)
                .map((occurrence) => occurrenceText(occurrence))
                .join(' '),
              5000
            );
          }
        }
      });
  }

  protected makePlanRouterLink(planId: string): string {
    return `../${planId}`;
  }

  onSearchTermChange(searchTerm: string): void {
    this.searchOnCardEntities.set(searchTerm);
  }

  onDropdownSearchChange(event: { dropdownId: string; term: string }): void {
    this.searchOnAddEntity.set(event.term);
  }

  /**
   * The job id alone, never the position: a job reported over the websocket is inserted at the
   * front, and a key holding the index would change for every row below it, rebuilding the whole
   * list and closing any dropdown open inside it.
   */
  protected trackChildPlan(child: ChildInfo): string {
    return child.jobId;
  }

  /**
   * Builds the card labels for an entity within the current plan: the
   * plan-scoped total (from `nbEntitiesPerType`) plus every plan-scoped extra
   * metric (from `extrasPerType`). Each key is translated and used as the
   * visible label text, and each value is grouped for the current locale (the
   * cards-list template renders labels as value over key). Mirrors the helper
   * in `entities-list.component.ts` but reads from the plan metadata rather than
   * the global entity metadata.
   */
  private buildLabels(
    entityName: string,
    plan: PlanMetadataModel,
    numberFormat: Intl.NumberFormat
  ): Record<string, string> {
    const entries: [string, number][] = [
      ['total', plan.nbEntitiesPerType[entityName] ?? 0],
      ...Object.entries(plan.extrasPerType?.[entityName] ?? {})
    ];

    return Object.fromEntries(
      entries.map(([key, value]) => [
        this.translateLabelKey(key),
        numberFormat.format(value)
      ])
    );
  }

  /**
   * Resolves a label key against `features.entities.labels.<key>`. When no
   * translation exists, ngx-translate returns the full key path, in which case
   * we fall back to a humanized version of the raw key (e.g. `fleet_size` ->
   * `Fleet size`).
   */
  private translateLabelKey(key: string): string {
    const translationKey = `features.entities.labels.${key}`;
    const translated = this.translate.instant(translationKey);
    if (translated === translationKey) {
      return capitalizeFirst(key.replace(/_/g, ' '));
    }
    return translated;
  }
}
